/**
 * AssetCatalog.tsx
 * Merged Project Type + Asset master — a two-pane master/detail screen on the
 * Sovereign-Emerald DataTableShell design system. The left rail selects a
 * project type; the right pane lists & manages that type's assets.
 */

import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Boxes, Plus } from 'lucide-react';
import { toast } from 'react-toastify';
import { Select } from 'antd';

import PageBreadcrumb from '@/shared/PageBreadCrumb';
import RightDrawer from '@/shared/components/form/RightDrawer';
import { DataTableShell } from '@/components/data-table';
import type { ActiveFilterChip } from '@/components/data-table';
import { SOVEREIGN } from '@/features/epc/utils/epcProjectVisual';
import { useAuth } from '@/context/AuthContext';

import ProjectTypeRail from '../components/ProjectTypeRail';
import { buildAssetColumns, AssetToggleStatusCell } from '../components/assetColumns';
import AssetForm from '../components/AssetForm';
import ProjectTypeForm from '@/features/projectType/form/ProjectTypeForm';

import {
  useProjectTypesQuery,
  useProjectTypeMutations,
  useAssetsByTypeQuery,
  useAssetMutations,
} from '../hooks/useAssetCatalog';
import type { ProjectType } from '@/services/projectTypeService';
import type { ProjectType as ProjectTypeFE } from '@/types/projectType';
import type { Asset } from '@/types/asset';

const ASSET_CATEGORY_OPTIONS = [
  'generation', 'collection', 'conversion', 'transmission', 'connection',
  'protection', 'structure', 'monitoring', 'safety', 'security', 'other',
].map((c) => ({ label: c[0].toUpperCase() + c.slice(1), value: c }));

const STATUS_OPTIONS = [
  { label: 'Active', value: 'active' },
  { label: 'Inactive', value: 'inactive' },
];

export default function AssetCatalog() {
  const { hasPermission } = useAuth();
  const canCreateType = hasPermission('projectType', 'create');
  const canUpdateType = hasPermission('projectType', 'update');
  const canCreateAsset = hasPermission('assetMaster', 'create');

  const [searchParams, setSearchParams] = useSearchParams();

  // ── Selection (synced to ?type=) ──────────────────────────────────────────
  const [selectedTypeId, setSelectedTypeId] = useState<string | null>(
    searchParams.get('type'),
  );

  // ── Rail state ────────────────────────────────────────────────────────────
  const [typeSearch, setTypeSearch] = useState('');

  // ── Asset table state ─────────────────────────────────────────────────────
  const [assetSearch, setAssetSearch] = useState('');
  // No status filter by default — show all assets for the type (matches the
  // previous Assets screen). Legacy/inactive assets were hidden when this
  // defaulted to 'active'. Users can still opt into a status filter.
  const [assetStatus, setAssetStatus] = useState<'active' | 'inactive' | null>(null);
  const [assetCategory, setAssetCategory] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [sortField, setSortField] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  // ── Drawers ───────────────────────────────────────────────────────────────
  const [typeFormOpen, setTypeFormOpen] = useState(false);
  const [editingType, setEditingType] = useState<ProjectType | null>(null);
  const [assetFormOpen, setAssetFormOpen] = useState(false);
  const [editingAsset, setEditingAsset] = useState<Asset | null>(null);

  // ── Data ──────────────────────────────────────────────────────────────────
  const { types, isFetching: typesLoading } = useProjectTypesQuery({
    search: typeSearch,
    status: null,
    category: null,
  });
  const typeMutations = useProjectTypeMutations();

  const { assets, totalDocs, isFetching: assetsLoading } = useAssetsByTypeQuery({
    projectTypeId: selectedTypeId,
    page, pageSize, search: assetSearch, status: assetStatus,
    category: assetCategory, sortField, sortOrder,
  });
  const assetMutations = useAssetMutations();

  // Auto-select first active type (or ?type=) once types are available.
  useEffect(() => {
    if (selectedTypeId && types.some((t) => (t._id || t.id) === selectedTypeId)) return;
    if (types.length === 0) return;
    const firstActive = types.find((t) => (t.status ?? t.projectTypeStatus) === 'active') ?? types[0];
    const id = firstActive._id || firstActive.id || null;
    setSelectedTypeId(id);
  }, [types, selectedTypeId]);

  // Keep ?type= in sync.
  useEffect(() => {
    if (!selectedTypeId) return;
    if (searchParams.get('type') === selectedTypeId) return;
    const next = new URLSearchParams(searchParams);
    next.set('type', selectedTypeId);
    setSearchParams(next, { replace: true });
  }, [selectedTypeId, searchParams, setSearchParams]);

  const selectedType = useMemo(
    () => types.find((t) => (t._id || t.id) === selectedTypeId) ?? null,
    [types, selectedTypeId],
  );

  const resetPage = () => setPage(1);
  const handleSelectType = (id: string) => {
    setSelectedTypeId(id);
    resetPage();
    setAssetSearch('');
  };

  // ── Project type toggle (rail) ──────────────────────────────────────────────
  const handleToggleType = async (type: ProjectType) => {
    const id = type._id || type.id || '';
    const isActive = (type.status ?? type.projectTypeStatus) === 'active';
    try {
      if (isActive) await typeMutations.deactivate.mutateAsync(id);
      else await typeMutations.activate.mutateAsync(id);
      toast.success(`Project type ${isActive ? 'deactivated' : 'activated'}`);
    } catch {
      toast.error('Failed to update project type status');
    }
  };

  // ── Form save handlers (forms self-submit; we invalidate + close) ───────────
  const handleTypeSaved = () => {
    setTypeFormOpen(false);
    setEditingType(null);
    typeMutations.invalidate();
  };
  const handleAssetSaved = () => {
    setAssetFormOpen(false);
    setEditingAsset(null);
    assetMutations.invalidate();
  };

  // ── Columns ─────────────────────────────────────────────────────────────────
  const columns = useMemo(
    () =>
      buildAssetColumns({
        onEdit: (a) => { setEditingAsset(a); setAssetFormOpen(true); },
        toggleCell: (a) => (
          <AssetToggleStatusCell
            asset={a}
            onConfirm={async () => { await assetMutations.toggleStatus.mutateAsync(a._id ?? a.id ?? ''); }}
          />
        ),
      }),
    [assetMutations.toggleStatus],
  );

  const activeFilters = useMemo<ActiveFilterChip[]>(() => {
    const chips: ActiveFilterChip[] = [];
    if (assetStatus) chips.push({ key: 'status', value: assetStatus, display: `Status: ${assetStatus}` });
    if (assetCategory) chips.push({ key: 'category', value: assetCategory, display: `Category: ${assetCategory}` });
    return chips;
  }, [assetStatus, assetCategory]);

  return (
    <div className="mx-auto max-w-9xl space-y-6">
      <PageBreadcrumb pageTitle="Asset Catalog" />

      <div className="grid grid-cols-1 lg:grid-cols-[20rem_minmax(0,1fr)] gap-6 items-start">
        {/* Left rail — desktop */}
        <div className="hidden lg:block">
          <ProjectTypeRail
            types={types}
            selectedId={selectedTypeId}
            onSelect={handleSelectType}
            search={typeSearch}
            onSearch={setTypeSearch}
            onAdd={() => { setEditingType(null); setTypeFormOpen(true); }}
            onEdit={(t) => { setEditingType(t); setTypeFormOpen(true); }}
            onToggle={handleToggleType}
            loading={typesLoading}
            canCreate={canCreateType}
            canUpdate={canUpdateType}
          />
        </div>

        {/* Mobile project-type picker */}
        <div className="lg:hidden">
          <label htmlFor="catalog-type-select" className="block text-xs font-semibold mb-1.5" style={{ color: SOVEREIGN.kpiCaption }}>
            Project Type
          </label>
          <Select
            id="catalog-type-select"
            className="w-full"
            value={selectedTypeId ?? undefined}
            onChange={(v) => handleSelectType(v)}
            placeholder="Select a project type"
            options={types.map((t) => ({ label: t.name, value: t._id || t.id || '' }))}
          />
        </div>

        {/* Right pane — assets for the selected type */}
        <DataTableShell<Asset>
          title={selectedType ? selectedType.name : 'Assets'}
          subtitle={selectedType ? `${totalDocs} ${totalDocs === 1 ? 'asset' : 'assets'}` : 'Select a project type'}
          icon={<Boxes size={20} />}
          primaryAction={
            canCreateAsset && selectedTypeId
              ? {
                  label: 'Add Asset',
                  icon: <Plus size={16} />,
                  onClick: () => { setEditingAsset(null); setAssetFormOpen(true); },
                }
              : undefined
          }
          columns={columns}
          data={assets}
          rowKey={(a) => a._id ?? a.id ?? ''}
          loading={assetsLoading}
          emptyState={{
            title: selectedTypeId ? 'No assets found' : 'No project type selected',
            description: selectedTypeId
              ? (activeFilters.length || assetSearch
                  ? 'Try adjusting your search or filters.'
                  : 'Get started by adding the first asset for this type.')
              : 'Choose a project type on the left to see its assets.',
          }}
          toolbar={{
            search: {
              value: assetSearch,
              onChange: (v) => { setAssetSearch(v); resetPage(); },
              placeholder: 'Search assets by name or code…',
            },
            filters: [
              { key: 'status', label: 'Status', options: STATUS_OPTIONS, value: assetStatus, onChange: (v) => { setAssetStatus((v as 'active' | 'inactive') || null); resetPage(); } },
              { key: 'category', label: 'Category', options: ASSET_CATEGORY_OPTIONS, value: assetCategory, onChange: (v) => { setAssetCategory((v as string) || null); resetPage(); } },
            ],
            activeFilters,
            onClearFilter: (chip) => {
              if (chip.key === 'status') setAssetStatus(null);
              if (chip.key === 'category') setAssetCategory(null);
              resetPage();
            },
            onClearAll: () => { setAssetStatus(null); setAssetCategory(null); resetPage(); },
          }}
          pagination={{
            page, pageSize, total: totalDocs,
            onChange: (p, ps) => { setPage(p); setPageSize(ps); },
          }}
          sortField={sortField}
          sortOrder={sortOrder}
          onSortChange={(field, order) => {
            setSortField(field ?? 'createdAt');
            setSortOrder(order ?? 'desc');
            resetPage();
          }}
        />
      </div>

      {/* Project Type form */}
      <RightDrawer
        open={typeFormOpen}
        title={editingType ? 'Edit Project Type' : 'Add Project Type'}
        onClose={() => { setTypeFormOpen(false); setEditingType(null); }}
      >
        <ProjectTypeForm
          editingItem={(editingType as unknown as ProjectTypeFE) ?? null}
          onAdd={handleTypeSaved}
          onClose={() => { setTypeFormOpen(false); setEditingType(null); }}
        />
      </RightDrawer>

      {/* Asset form */}
      <RightDrawer
        open={assetFormOpen}
        title={editingAsset ? 'Edit Asset' : 'Add New Asset'}
        onClose={() => { setAssetFormOpen(false); setEditingAsset(null); }}
      >
        <div className="p-1">
          <AssetForm
            editingAsset={editingAsset}
            projectTypes={types as unknown as ProjectTypeFE[]}
            defaultProjectTypeId={selectedTypeId ?? undefined}
            onAdd={handleAssetSaved}
            onClose={() => { setAssetFormOpen(false); setEditingAsset(null); }}
          />
        </div>
      </RightDrawer>
    </div>
  );
}
